/**
 * Query Keys Constants
 *
 * Centralized query key definitions following TanStack Query best practices
 * Structure: [domain, ...filters/params]
 */

export const queryKeys = {
  // Home
  home: {
    all: ['home'] as const,
    banner: () => [...queryKeys.home.all, 'banner'] as const,
    terms: () => [...queryKeys.home.all, 'terms'] as const,
  },

  // Tracks
  tracks: {
    all: ['tracks'] as const,
    lists: () => [...queryKeys.tracks.all, 'list'] as const,
    list: (filters: {
      ordering?: string;
      status?: string;
      page?: number;
      pageSize?: number;
      search?: string;
    }) => [...queryKeys.tracks.lists(), filters] as const,
    details: () => [...queryKeys.tracks.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.tracks.details(), id] as const,
  },

  // Artists
  artists: {
    all: ['artists'] as const,
    lists: () => [...queryKeys.artists.all, 'list'] as const,
    list: (filters: {
      ordering?: string;
      page?: number;
      pageSize?: number;
      search?: string;
    }) => [...queryKeys.artists.lists(), filters] as const,
    details: () => [...queryKeys.artists.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.artists.details(), id] as const,
    tracks: (id: number, filters: {
      ordering?: string;
      page?: number;
      pageSize?: number;
    }) => [...queryKeys.artists.detail(id), 'tracks', filters] as const,
  },

  // Cart
  cart: {
    all: ['cart'] as const,
    items: () => [...queryKeys.cart.all, 'items'] as const,
    summary: () => [...queryKeys.cart.all, 'summary'] as const,
  },

  // Purchases
  purchases: {
    all: ['purchases'] as const,
    lists: () => [...queryKeys.purchases.all, 'list'] as const,
    list: (page?: number) => [...queryKeys.purchases.lists(), page] as const,
    details: () => [...queryKeys.purchases.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.purchases.details(), id] as const,
    certificate: (orderId: string) => [...queryKeys.purchases.all, 'certificate', orderId] as const,
  },

  // Payment
  payment: {
    all: ['payment'] as const,
    success: (params: { paymentKey: string; orderId: string; amount: number }) =>
      [...queryKeys.payment.all, 'success', params] as const,
    failure: (code: string, message: string) =>
      [...queryKeys.payment.all, 'failure', { code, message }] as const,
  },

  // Auth
  auth: {
    all: ['auth'] as const,
    user: () => [...queryKeys.auth.all, 'user'] as const,
    profile: () => [...queryKeys.auth.all, 'profile'] as const,
  },

  // Downloads
  downloads: {
    all: ['downloads'] as const,
    track: (trackId: number, orderId: number) =>
      [...queryKeys.downloads.all, 'track', { trackId, orderId }] as const,
  },
} as const;
